import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  EntitySubCategoryApi,
  EntitySubCategoryType,
  SwitchEntitySubCategory,
  SwitchEntitySubCategoryForCollaboratorProducts,
  UpdateEntitySubCategorySort,
} from 'api';

export const findEntitySubCategoryWithProductSize = createAsyncThunk(
  'entitySubCategory/findEntitySubCategoryWithProductSize',
  async (query: any, { rejectWithValue }) => {
    try {
      const response = await EntitySubCategoryApi.filter(query);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const createEntitySubCategory = createAsyncThunk(
  'entitySubCategory/createEntitySubCategory',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await EntitySubCategoryApi.create(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeEntitySubCategory = createAsyncThunk(
  'entitySubCategory/removeEntitySubCategory',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await EntitySubCategoryApi.remove(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntitySubCategory = createAsyncThunk(
  'entitySubCategory/updateEntitySubCategory',
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await EntitySubCategoryApi.update(params);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntitySubCategorySort = createAsyncThunk(
  'entitySubCategory/updateEntitySubCategorySort',
  async (params: UpdateEntitySubCategorySort, { rejectWithValue }) => {
    try {
      const response = await EntitySubCategoryApi.updateSort(params);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const switchEntitySubCategory = createAsyncThunk(
  'entitySubCategory/switchEntitySubCategory',
  async (params: SwitchEntitySubCategory, { rejectWithValue }) => {
    try {
      const response = await EntitySubCategoryApi.switch(params);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const switchEntitySubCategoryForCollaboratorProducts = createAsyncThunk(
  'entitySubCategory/switchEntitySubCategoryForCollaboratorProducts',
  async (
    params: SwitchEntitySubCategoryForCollaboratorProducts,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntitySubCategoryApi.switchForCollaboratorProducts(
        params
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForAddMultiProducts = createAsyncThunk(
  'entitySubCategory/findProductsForAddMultiProducts',
  async (params: { id: string; query: any }, { rejectWithValue }) => {
    try {
      const { id, query } = params;
      const response = await EntitySubCategoryApi.findForAddMultiProducts(
        id,
        query
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductsForAddMultiCollaboratorProducts = createAsyncThunk(
  'entitySubCategory/findProductsForAddMultiCollaboratorProducts',
  async (params: { id: string; query: any }, { rejectWithValue }) => {
    try {
      const { id, query } = params;
      const response =
        await EntitySubCategoryApi.findForAddMultiCollaboratorProducts(
          id,
          query
        );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntitySubCategoryWithEntity = createAsyncThunk(
  'entitySubCategory/findEntitySubCategoryWithEntity',
  async (any, { rejectWithValue }) => {
    try {
      const response = await EntitySubCategoryApi.findWithEntity();
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

interface IState {
  updatedEntitySubCategory: UpdatedEntitySubCategoryType;
  updatedEntitySubCategoryCollaboratorProduct: UpdatedEntitySubCategoryForCollaboratorProductType;
  list: Array<EntitySubCategoryType>;
  isLoaded: boolean;
}

const initialState: IState = {
  updatedEntitySubCategory: {
    productEntitySubCategoryIDs: [],
    targetEntityCategoryID: '',
    targetEntitySubCategoryID: '',
  },
  updatedEntitySubCategoryCollaboratorProduct: {
    collaboratorProductEntitySubCategoryIDs: [],
    targetEntityCategoryID: '',
    targetEntitySubCategoryID: '',
  },
  list: [],
  isLoaded: false,
};

const entitySubCategorySlice = createSlice({
  name: 'entitySubCategory',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(switchEntitySubCategory.fulfilled, (state, action) => {
      const { data } = action.payload;
      state.updatedEntitySubCategory = data;
    });

    builder.addCase(
      switchEntitySubCategoryForCollaboratorProducts.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.updatedEntitySubCategoryCollaboratorProduct = data;
      }
    );

    builder.addCase(
      findEntitySubCategoryWithEntity.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.list = data;
      }
    );
  },
});

const { reducer: entitySubCategoryReducer } = entitySubCategorySlice;

export default entitySubCategoryReducer;

interface UpdatedEntitySubCategoryType {
  productEntitySubCategoryIDs: string[];
  targetEntitySubCategoryID: string;
  targetEntityCategoryID: string;
}

interface UpdatedEntitySubCategoryForCollaboratorProductType {
  collaboratorProductEntitySubCategoryIDs: string[];
  targetEntitySubCategoryID: string;
  targetEntityCategoryID: string;
}
